import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import { getNotes, getNoteCategories } from '../../api/notes';
import './Notes.css';

/**
 * 기술노트 목록 / 카테고리 허브.
 *
 * `/notes` 는 전체, `/notes/{카테고리}` 는 그 카테고리만 보여 준다.
 * 글 상세는 평면 `/notes/{slug}` 라 이 컴포넌트와 같은 자리를 쓴다 —
 * 어느 쪽인지는 카테고리 목록에 있는 slug 인지로 가른다(라우터가 아니라 데이터로).
 */
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

const Notes = () => {
    const { segment } = useParams();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const cats = await getNoteCategories();
            setCategories(cats);
            setNotes(await getNotes(segment || undefined));
        } catch (err) {
            console.error('기술노트를 불러오지 못했습니다', err);
            setNotes([]);
        } finally {
            setLoading(false);
        }
    }, [segment]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const cur = categories.find((c) => c.slug === segment);
        document.title = cur ? `${cur.label} | 기술노트 | 가까운솔루션` : '기술노트 | 가까운솔루션';
        return () => { document.title = '가까운솔루션'; };
    }, [segment, categories]);

    const current = categories.find((c) => c.slug === segment) || null;

    return (
        <div className="notes-page">
            <div className="page-hero">
                <div className="container">
                    <h1 className="page-title">{current ? current.label : '기술노트'}</h1>
                    <p className="page-subtitle">
                        {current
                            ? (current.description || `${current.label}에 대해 현장에서 정리한 글입니다.`)
                            : <>솔루션을 고르기 전에 알아 두면 좋은 것들을 정리합니다.<br />제조사별 특징, 제품 차이, 도입할 때 실제로 부딪히는 문제들입니다.</>}
                    </p>
                </div>
            </div>

            <section className="notes-body">
                <div className="container">
                    <nav className="notes-tabs" aria-label="기술노트 카테고리">
                        <button
                            type="button"
                            className={`notes-tab ${!segment ? 'is-active' : ''}`}
                            onClick={() => navigate('/notes')}
                        >
                            전체
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                type="button"
                                className={`notes-tab ${segment === c.slug ? 'is-active' : ''}`}
                                onClick={() => navigate(`/notes/${c.slug}`)}
                            >
                                {c.label}
                                {c.noteCount > 0 && <span className="notes-tab-count">{c.noteCount}</span>}
                            </button>
                        ))}
                    </nav>

                    {loading ? (
                        <p className="notes-empty">불러오는 중…</p>
                    ) : notes.length === 0 ? (
                        <p className="notes-empty">
                            아직 발행된 글이 없습니다.
                        </p>
                    ) : (
                        <ul className="notes-list">
                            {notes.map((n) => (
                                <li key={n.slug} className="note-card">
                                    <Link to={`/notes/${n.slug}`} className="note-card-link">
                                        {n.heroKey && (
                                            <div className="note-card-thumb">
                                                <img src={`/uploads/${n.heroKey}`} alt="" loading="lazy" />
                                            </div>
                                        )}
                                        <div className="note-card-body">
                                            {n.categoryLabel && <span className="note-card-cat">{n.categoryLabel}</span>}
                                            <h2 className="note-card-title">{n.title}</h2>
                                            {n.summary && <p className="note-card-summary">{n.summary}</p>}
                                            <div className="note-card-meta">
                                                {n.authorName && <span>{n.authorName}</span>}
                                                {n.publishedAt && <time dateTime={n.publishedAt}>{fmtDate(n.publishedAt)}</time>}
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <UnifiedInquirySection />
        </div>
    );
};

export default Notes;
